import { buildSectionSelector, normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, onDocumentReady } from '@theme/utilities';

/**
 * Renders already in flight, keyed by normalized section id.
 *
 * `data-hydrated` is only set once a render resolves, so two callers arriving
 * during that window would both pass the guard and fetch the section twice.
 * `hydrateNow` makes that a real possibility: it can fire while the idle
 * callback's render is still open.
 *
 * @type {Map<string, Promise<void>>}
 */
const inFlight = new Map();

/**
 * Hydrates a section using the Section Rendering API preserving states.
 * Only updates elements with data-hydration-key attribute. This ensures that non-targeted nodes do not have their markup/state updated unnecessarily.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
async function hydrateSection(sectionId, url) {
  const normalizedId = normalizeSectionId(sectionId);
  const section = document.getElementById(buildSectionSelector(normalizedId));

  if (!section || section.dataset.hydrated === 'true') {
    return;
  }

  const pending = inFlight.get(normalizedId);
  if (pending) return pending;

  const render = sectionRenderer
    .renderSection(normalizedId, { cache: false, url, mode: 'hydration' })
    .then(() => {
      section.dataset.hydrated = 'true';
    })
    .finally(() => {
      inFlight.delete(normalizedId);
    });

  inFlight.set(normalizedId, render);

  return render;
}

/**
 * Hydrates a section immediately, without waiting for an idle period. For the
 * case where the shopper has just reached for something the hydration pass is
 * responsible for rendering.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
export async function hydrateNow(sectionId, url) {
  return hydrateSection(sectionId, url);
}

/**
 * Hydrates a section using the Section Rendering API preserving states, when
 * the DOM is ready.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
export async function hydrate(sectionId, url) {
  onDocumentReady(() => {
    // The `timeout` is the point. A phone loading this page never really goes
    // idle — pixels, app embeds and image decoding keep the main thread busy in
    // short bursts — so a bare requestIdleCallback can be starved for many
    // seconds, and the parts of a section that only exist in the hydration
    // render appear to load "when you touch the page". With a deadline the
    // browser has to run the callback whether or not it ever found an idle
    // period.
    requestIdleCallback(() => hydrateSection(sectionId, url), { timeout: 1500 });
  });
}
